import { useSignals } from '@preact/signals-react/runtime'
import type { GamepadEmulation, EmulatedControllerId } from '../../../../deps/debug/gamepadEmulation/gamepadEmulation.types'

function controllerColorClass(id: EmulatedControllerId): string {
  if (id === 'X1') return 'bg-red-500'
  if (id === 'X2') return 'bg-blue-500'
  return 'bg-lime-500'
}

function controllerText(id: EmulatedControllerId): string {
  if (id === 'X1') return 'Red'
  if (id === 'X2') return 'Blue'
  return 'Lime'
}

function pulseIfConnected(
  emu: GamepadEmulation,
  controllerId: EmulatedControllerId,
  connected: boolean,
  buttonIndex: number
): void {
  if (!connected) return
  emu.pulseButton(controllerId, buttonIndex)
}

export function GamepadEmulationDebug({ gamepadEmulation }: { gamepadEmulation: GamepadEmulation }) {
  useSignals()

  const isEnabled = gamepadEmulation.isEnabled.value
  const selectedControllerId = gamepadEmulation.selectedControllerId.value
  const controllers = gamepadEmulation.controllers.value

  const selectedGamepadId: EmulatedControllerId | null =
    selectedControllerId && selectedControllerId !== 'K' ? selectedControllerId : null

  const selectedInfo = selectedGamepadId
    ? (controllers.find((c: any) => c.id === selectedGamepadId) || null)
    : null
  const selectedConnected = Boolean(selectedInfo?.connected)

  const toggleSelectedConnected = (next: boolean) => {
    if (!selectedGamepadId) return
    if (next) {
      gamepadEmulation.connect(selectedGamepadId)
      return
    }
    gamepadEmulation.disconnect(selectedGamepadId)
  }

  const buttonBase =
    'w-9 h-9 rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-semibold disabled:opacity-40 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800'

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between p-2 border-b">
        <h3 className="font-semibold text-sm">Emulation</h3>
        <div className="flex items-center gap-3">
          <label className="text-xs flex items-center gap-2">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => (e.target.checked ? gamepadEmulation.enable() : gamepadEmulation.disable())}
            />
            <span className="font-medium">Enabled</span>
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {/* Controller selection */}
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2">
            {/* Keyboard */}
            <button
              type="button"
              className={[
                'h-9 w-20 rounded text-xs font-semibold',
                'bg-gray-400 dark:bg-gray-600 text-gray-900 dark:text-gray-100',
                'border-2 border-black/25 dark:border-white/25',
                selectedControllerId === 'K' ? 'ring-4 ring-white ring-offset-2 ring-offset-black/50 shadow-sm scale-[1.02]' : '',
              ].join(' ')}
              onClick={() => {
                gamepadEmulation.selectedControllerId.value = 'K'
              }}
              title="Keyboard (K)"
            >
              K
            </button>

            {controllers.map((c: any) => {
              const isSelected = c.id === selectedControllerId
              const isConnected = c.connected
              const borderClass = 'border-2 border-black/25 dark:border-white/25'
              const selectedClass = isSelected
                ? 'ring-4 ring-white ring-offset-2 ring-offset-black/50 shadow-sm scale-[1.02]'
                : ''
              const dimClass = isConnected ? '' : 'opacity-35'

              return (
                <button
                  key={c.id}
                  type="button"
                  className={[
                    'h-9 w-20 rounded text-xs font-semibold text-white',
                    controllerColorClass(c.id),
                    borderClass,
                    selectedClass,
                    dimClass,
                  ].join(' ')}
                  onClick={() => {
                    gamepadEmulation.selectedControllerId.value = c.id
                  }}
                  disabled={false}
                  title={`${c.id} — ${c.label}`}
                >
                  {controllerText(c.id)}
                </button>
              )
            })}
          </div>

          {/* Single toggle for selected controller connectivity */}
          <div className="flex items-center justify-center">
            <label className="text-xs flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedConnected}
                onChange={(e) => toggleSelectedConnected(e.target.checked)}
                disabled={!selectedControllerId || selectedControllerId === 'K'}
              />
              <span className="font-medium">
                {!selectedControllerId ? 'No selection' : selectedControllerId === 'K' ? 'Keyboard' : selectedConnected ? 'Connected' : 'Disconnected'}
              </span>
            </label>
          </div>
        </div>

        {/* Virtual controller controls */}
        <div className="flex items-center justify-between gap-3">
          {/* D-pad */}
          <div className="flex flex-col items-center gap-1">
            <div className="grid grid-cols-3 grid-rows-3 gap-1">
              <div />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 12)}
                title="D-pad Up"
              >
                ↑
              </button>
              <div />

              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 14)}
                title="D-pad Left"
              >
                ←
              </button>
              <div className="w-9 h-9" />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 15)}
                title="D-pad Right"
              >
                →
              </button>

              <div />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 13)}
                title="D-pad Down"
              >
                ↓
              </button>
              <div />
            </div>
          </div>

          {/* Menu */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="w-10 h-10 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-semibold disabled:opacity-40"
              disabled={!selectedConnected}
              onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 9)}
              title="Menu (M)"
            >
              M
            </button>
          </div>

          {/* ABXY */}
          <div className="flex flex-col items-center gap-1">
            <div className="grid grid-cols-3 grid-rows-3 gap-1">
              <div />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 3)}
                title="Y (button 3)"
              >
                Y
              </button>
              <div />

              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 2)}
                title="X (configure)"
              >
                X
              </button>
              <div className="w-9 h-9" />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 1)}
                title="B (exit)"
              >
                B
              </button>

              <div />
              <button
                type="button"
                className={buttonBase}
                disabled={!selectedConnected}
                onClick={() => selectedGamepadId && pulseIfConnected(gamepadEmulation, selectedGamepadId, selectedConnected, 0)}
                title="A (select)"
              >
                A
              </button>
              <div />
            </div>
          </div>
        </div>

        {/* Extra */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div>
            Selected: <span className="font-mono">{selectedControllerId || '-'}</span>
          </div>
          <button
            type="button"
            className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-40"
            onClick={() => gamepadEmulation.disconnectAll()}
            disabled={!isEnabled}
            title="Disconnect all emulated controllers"
          >
            Disconnect all
          </button>
        </div>
      </div>
    </div>
  )
}


